// Persistência do catálogo de profissões.
//
// A tela de administração montava a profissão inteira em memória e chamava um
// `setState`: editar um documento parecia funcionar e sumia no F5. Aqui fica a
// gravação de verdade, contra as três tabelas que o catálogo usa desde a
// migration 0022 — `profissoes`, `profissao_documentos` e `profissao_campos` —
// mais `treinamentos`, que é o catálogo por profissão.
//
// Quem pode escrever é o superusuário: as policies exigem `e_superusuario()`, e
// esta camada não tenta adivinhar isso — se o banco recusar, o erro sobe.

import { supabase } from '@/lib/supabase';
import { invalidarCatalogo } from '@/lib/catalogo';
import type { Profissao } from '@/lib/profissoes';

/**
 * Id do filho, sempre prefixado pela profissão.
 *
 * O id que vem do catálogo já chega assim (`prof-porteiro:doc-cnh`); o que a
 * tela acabou de criar vem solto (`doc-1724…`). Prefixar os dois evita que o
 * mesmo documento exigido por duas profissões colida — e foi por não prefixar
 * que a migration 0022 perdeu uma linha na primeira tentativa.
 */
const idDoFilho = (profissaoId: string, id: string): string =>
  id.startsWith(`${profissaoId}:`) ? id : `${profissaoId}:${id}`;

/**
 * Substitui o conjunto de filhos de uma profissão.
 *
 * Apaga o que saiu e grava o que ficou, em vez de tentar um diff campo a campo:
 * o catálogo é pequeno, e a operação inteira precisa acabar num estado que
 * corresponde exatamente ao que a tela mostra.
 */
async function substituirFilhos(
  tabela: 'profissao_documentos' | 'profissao_campos',
  profissaoId: string,
  linhas: Record<string, unknown>[],
): Promise<void> {
  const idsQueFicam = linhas.map((l) => l.id as string);

  const remocao = supabase.from(tabela).delete().eq('profissao_id', profissaoId);
  const { error: erroRemocao } = idsQueFicam.length
    ? await remocao.not('id', 'in', `(${idsQueFicam.map((i) => `"${i}"`).join(',')})`)
    : await remocao;

  if (erroRemocao) throw new Error(erroRemocao.message);

  if (linhas.length === 0) return;

  const { error } = await supabase.from(tabela).upsert(linhas, { onConflict: 'id' });
  if (error) throw new Error(error.message);
}

export async function salvarProfissao(p: Profissao): Promise<void> {
  const { error: erroProfissao } = await supabase.from('profissoes').upsert(
    {
      id: p.id,
      nome: p.nome,
      descricao: p.descricao,
      categoria: p.categoria,
      ativo: p.ativo,
      observacoes: p.observacoes ?? null,
      requer_registro_profissional: p.requerRegistroProfissional,
      // A constraint `profissoes_registro_check` recusa sigla sem exigência: o
      // valor de uma decisão anterior não pode ficar guardado esperando alguém
      // marcar a caixa de novo.
      tipo_registro_profissional: p.requerRegistroProfissional
        ? p.tipoRegistroProfissional?.trim() || null
        : null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'id' },
  );
  if (erroProfissao) throw new Error(erroProfissao.message);

  await substituirFilhos(
    'profissao_documentos',
    p.id,
    (p.documentos ?? []).map((d, ordem) => ({
      id: idDoFilho(p.id, d.id),
      profissao_id: p.id,
      nome: d.nome,
      tipo: d.tipo,
      obrigatorio: d.obrigatorio,
      ordem: (ordem + 1) * 10,
      editado_em: new Date().toISOString(),
    })),
  );

  await substituirFilhos(
    'profissao_campos',
    p.id,
    (p.campos ?? []).map((c, ordem) => ({
      id: idDoFilho(p.id, c.id),
      profissao_id: p.id,
      nome: c.nome,
      tipo: c.tipo,
      obrigatorio: c.obrigatorio,
      ordem: (ordem + 1) * 10,
      editado_em: new Date().toISOString(),
    })),
  );

  // ─── Treinamentos ──────────────────────────────────────────────────
  //
  // O treinamento deixou de ser filho da profissão (migration 0047): o
  // destinatário virou N:N, e a mesma NR-35 vale para o eletricista, o
  // hidráulico e o elevadorista sem virar três linhas com três ids.
  //
  // Isso muda o que "salvar a profissão" significa aqui. Antes, os treinamentos
  // que sumissem da tela eram **apagados** — o que hoje seria destruir o
  // vínculo com as outras profissões junto. Agora a tela edita cada treinamento
  // e o conjunto de destinatários dele; tirar um da lista desta profissão só
  // remove esta profissão da lista dele.
  await salvarTreinamentosDaProfissao(p);

  // O backend versiona o catálogo sozinho (migration 0023); aqui é o cache de
  // sessão que precisa saber que mudou.
  invalidarCatalogo('profissoes');
  invalidarCatalogo('treinamentos');
}

/**
 * Grava os treinamentos que a tela mostra para esta profissão.
 *
 * Cada um passa por `salvar_treinamento()`, a RPC da migration 0047: ela grava
 * o treinamento e os destinatários na **mesma transação**, que é o que a
 * constraint trigger exige. Um `upsert` direto na tabela seria recusado no
 * commit, porque o treinamento existiria por um instante sem destinatário
 * nenhum.
 */
async function salvarTreinamentosDaProfissao(p: Profissao): Promise<void> {
  const treinamentos = p.treinamentos ?? [];

  for (const t of treinamentos) {
    // Treinamento que vale para qualquer profissão não carrega lista — e a tela
    // desta profissão não pode reescrever os destinatários dele, que pertencem
    // ao catálogo inteiro.
    const destinatarios = t.aplicaAQualquerProfissao
      ? []
      : Array.from(new Set([...(t.profissoes ?? []), p.id]));

    const { error } = await supabase.rpc('salvar_treinamento', {
      p_treinamento: {
        // Id novo nasce prefixado pela profissão que o criou, como os demais
        // filhos do catálogo; o que já existe mantém o seu.
        id: idDoFilho(p.id, t.id),
        nome: t.nome,
        norma: t.norma ?? null,
        url_legislacao: t.urlLegislacao ?? null,
        abrangencia: t.abrangencia ?? 'nacional',
        estados: t.estados ?? null,
        cidades: t.cidades ?? null,
        vigencia_valor: t.vigenciaValor,
        vigencia_unidade: t.vigenciaUnidade,
        prazo_valor: t.prazoValor ?? null,
        prazo_unidade: t.prazoUnidade ?? null,
        carga_horaria: t.cargaHoraria ?? null,
        obrigatorio: t.obrigatorio,
        descricao: t.descricao ?? null,
        dias_antecedencia_aviso: t.diasAntecedenciaAviso ?? null,
        tempo_medio_geracao_valor: t.tempoMedioGeracaoValor ?? null,
        tempo_medio_geracao_unidade: t.tempoMedioGeracaoUnidade ?? null,
        aplica_a_qualquer_profissao: t.aplicaAQualquerProfissao,
        profissoes: destinatarios,
      },
    });
    if (error) throw new Error(error.message);
  }
}

/**
 * Desvincula um treinamento desta profissão.
 *
 * **Não apaga o treinamento.** A NR-35 continua valendo para as outras
 * profissões; o que sai é uma linha de `treinamento_profissoes`. Se esta era a
 * última, o banco recusa — a constraint da secao 4.2 não deixa um treinamento
 * ficar sem destinatário —, e o erro sobe para a tela dizer que é preciso
 * marcar "aplica a qualquer profissão" ou excluir o treinamento.
 */
export async function desvincularTreinamento(
  treinamentoId: string,
  profissaoId: string,
): Promise<void> {
  const { error } = await supabase
    .from('treinamento_profissoes')
    .delete()
    .eq('treinamento_id', treinamentoId)
    .eq('profissao_id', profissaoId);

  if (error) throw new Error(error.message);

  invalidarCatalogo('profissoes');
  invalidarCatalogo('treinamentos');
}

/**
 * Desativa a profissão em vez de removê-la.
 *
 * Funcionário cadastrado guarda o id da profissão em `profissoes_ids`, e
 * treinamento concluído aponta para o catálogo. Apagar deixaria os dois
 * pendurados; desativar tira a profissão das escolhas novas e preserva o que já
 * foi registrado.
 */
export async function desativarProfissao(id: string): Promise<void> {
  const { error } = await supabase
    .from('profissoes')
    .update({ ativo: false, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw new Error(error.message);

  invalidarCatalogo('profissoes');
}
